//
// PushProvider - Unified push transport for WebSocket and WebPush
//
// Manages both WebSocket connection and ServiceWorker (WebPush) communication.
// Provides a unified subscribe(eventType, callback, filter) API.
// Events from either source are dispatched through the same mechanism.
//
// WebSocket credentials are requested from back-town (/ws/init)
//

import PropTypes from 'prop-types';
import {
  useEffect,
  useCallback,
  useMemo,
  useReducer,
  useRef,
  useState,
} from 'react';

import {
  config,
  displayJson,
  logDev,
  logError,
  wsRetryDelayMs,
} from '../../utils';
import useAuth from '../auth/useAuth';

import PushContext from './PushContext';
import usePauseWsDeliveryWhileUnfocused from './usePauseWsDeliveryWhileUnfocused';

//
// Encode rlid for WebSocket protocol header
//
// The Sec-WebSocket-Protocol header value must be a valid HTTP token (RFC 7230).
// Allowed characters: ! # $ % & ' * + - . ^ _ ` | ~ 0-9 A-Z a-z
// PxUid's used to use 0-9 A-Z a-z ( ) but parentheses are NOT valid HTTP token characters.
// We substitute: '(' -> '-' and ')' -> '_' for transmission.
// Server decodes back to original rlid.
//
// We trim to 5 chars because api-web-anon trims rlid for end users (privacy).
// Using a consistent 5-char prefix ensures the sanity check works for all user types.
//
const trimAndEncodeRlidForProtocol = (rlid) => rlid.slice(0, 5).replace(/\(/g, '-').replace(/\)/g, '_');

// Returning to the tab several times in a row asks the server once
const retryOnReturnMinGapMs = 30 * 1000;

// LocalStorage keys for WS credentials
const WS_JWT_KEY = 'ws_jwt';
const WS_URL_KEY = 'ws_url';
const WS_EXPIRES_KEY = 'ws_expires';
const WS_RLID_KEY = 'ws_rlid';

const initialState = {
  // WebSocket state
  wsConnected: false,
  wsConnecting: false,
  // hold off connecting after disconnect, error, etc:
  wsHold: false,
  wsError: null,
  // ServiceWorker state
  swAvailable: false,
};

const matchesFilter = (message, filter) => (
  Object.entries(filter).every(([key, value]) => (message[key] === value))
);

const stateReducer = (state, action) => {
  switch (action.type) {
    case 'wsConnecting':
      return {
        ...state,
        wsConnecting: true,
        wsConnected: false,
        wsError: null,
      };

    case 'wsConnected':
      return {
        ...state,
        wsConnecting: false,
        wsConnected: true,
        wsHold: false,
        wsError: null,
      };

    case 'wsDisconnected':
      return {
        ...state,
        wsConnecting: false,
        wsConnected: false,
        wsHold: true,
        wsError: null,
      };

    case 'wsIdle':
      return {
        ...state,
        wsHold: false,
      };

    case 'wsError':
      return {
        ...state,
        wsConnecting: false,
        wsConnected: false,
        wsHold: true,
        wsError: action.error,
      };

    case 'swAvailable':
      return {
        ...state,
        swAvailable: action.available,
      };

    default:
      logError(
        `PushProvider - unknown action.type=${action.type}`,
        'PushProvider.reducer.unknownAction',
        { actionType: action?.type },
      );
      return state;
  }
};

// Get stored WS credentials from localStorage. Storage is shared by every tab of
// this browser, so credentials minted for another role are not ours to present.
const getStoredWsCredentials = (rlid) => {
  const jwt = localStorage.getItem(WS_JWT_KEY);
  const wsUrl = localStorage.getItem(WS_URL_KEY);
  const expiresStr = localStorage.getItem(WS_EXPIRES_KEY);

  if ((!jwt) || (!wsUrl) || (!expiresStr)) {
    return null;
  }

  if (localStorage.getItem(WS_RLID_KEY) !== rlid) {
    return null;
  }

  const expires = new Date(expiresStr);
  // Check if JWT is expired (with 1 minute buffer)
  if (expires.getTime() < (Date.now() + (60 * 1000))) {
    return null;
  }

  return { jwt, wsUrl, expires };
};

// Store WS credentials in localStorage
const storeWsCredentials = (jwt, wsUrl, expires, rlid) => {
  localStorage.setItem(WS_JWT_KEY, jwt);
  localStorage.setItem(WS_URL_KEY, wsUrl);
  localStorage.setItem(WS_EXPIRES_KEY, expires);
  localStorage.setItem(WS_RLID_KEY, rlid);
};

// Clear WS credentials from localStorage
const clearWsCredentials = () => {
  localStorage.removeItem(WS_JWT_KEY);
  localStorage.removeItem(WS_URL_KEY);
  localStorage.removeItem(WS_EXPIRES_KEY);
  localStorage.removeItem(WS_RLID_KEY);
};

function PushProvider(props) {
  const { children } = props;
  const { auth } = useAuth();
  const [state, dispatch] = useReducer(stateReducer, initialState);
  const wsRef = useRef(null);
  const wsBusyRef = useRef(false);
  const wsFailCountRef = useRef(0);
  const wsRlidRef = useRef();
  const wsNoChannelRlidRef = useRef();
  const listenersRef = useRef(new Map());
  const holdTimeoutRef = useRef();
  const credentialFailCountRef = useRef(0);
  const lastAttemptAtRef = useRef(0);
  const [wasAuthenticated, setWasAuthenticated] = useState(false);

  const {
    info: authInfo,
    authStatus,
    cstmrPost,
    server: { recheckEndedSession },
  } = auth;

  const {
    wsConnected,
    wsConnecting,
    wsHold,
    wsError,
    swAvailable,
  } = state;

  const wsPaused = usePauseWsDeliveryWhileUnfocused(wsRef, wsConnected);

  //
  // Unified event dispatch - used by both WebSocket and ServiceWorker
  //
  const dispatchEvent = useCallback(
    (eventType, data, source) => {
      if (config.dev || config.demo) {
        console.log(
          `[push] ${eventType} csid=${data.csid ?? '-'}${
            ((data.msgId !== undefined) && (data.msgId !== null)) ? ` msgId=${data.msgId}` : ''
          } via ${source}`,
        );
      }

      const listeners = listenersRef.current.get(eventType);
      if (listeners) {
        listeners.forEach(({ filter, callback }) => {
          if ((!filter) || matchesFilter(data, filter)) {
            callback(data);
          }
        });
      }
    },
    [],
  );

  //
  // Subscribe to events (unified API for both WS and WebPush)
  //
  const subscribe = useCallback(
    (eventType, callback, filter) => {
      if (!listenersRef.current.has(eventType)) {
        listenersRef.current.set(eventType, []);
      }

      const entry = { filter, callback };
      listenersRef.current.get(eventType).push(entry);

      // Return unsubscribe function
      return () => {
        const listeners = listenersRef.current.get(eventType);
        if (listeners) {
          const idx = listeners.indexOf(entry);
          if (idx >= 0) listeners.splice(idx, 1);
        }
      };
    },
    [],
  );

  //
  // WebSocket connection management
  //
  const endWsHold = useCallback(
    () => {
      if (!holdTimeoutRef.current) return;

      clearTimeout(holdTimeoutRef.current);
      holdTimeoutRef.current = undefined;
      wsBusyRef.current = false;
      dispatch({ type: 'wsIdle' });
    },
    [],
  );

  const declareWsDisconnectAndHold = useCallback(
    (err, holdMs = wsRetryDelayMs({})) => {
      wsRef.current = null;
      if (err) dispatch({
        type: 'wsError',
        error: err,
      });
      else dispatch({ type: 'wsDisconnected' });

      if (holdTimeoutRef.current) {
        clearTimeout(holdTimeoutRef.current);
      }

      holdTimeoutRef.current = setTimeout(
        () => {
          holdTimeoutRef.current = undefined;
          wsBusyRef.current = false;
          dispatch({ type: 'wsIdle' });
        },
        holdMs,
      );
    },
    [],
  );

  const wsDisconnect = useCallback(
    () => {
      if (wsRef.current) {
        logDev('PushProvider WebSocket disconnecting...');
        wsRef.current.close();
      }
      declareWsDisconnectAndHold();
    },
    [declareWsDisconnectAndHold],
  );

  // Request new WS session credentials from back-town
  const requestWsCredentials = useCallback(
    async () => {
      logDev('PushProvider: Requesting new WS credentials...');

      try {
        const res = await cstmrPost({
          urlPath: config.town.back.ws.init,
          payload: {},
          log: 'WebSocket init',
        });

        if ((!res?.body?.rslt?.data) || (res.body.rslt.data.length === 0)) {
          logError(
            'PushProvider: Invalid response from /ws/init',
            'PushProvider.requestWsCredentials.invalidResponse',
            { resStatus: res?.status },
          );
          return null;
        }

        const { jwt, wsUrl, expires } = res.body.rslt.data[0];

        // The server reported it on its side: nothing to log from here
        if (res.body.rslt.data[0].wsRefused) {
          logDev('PushProvider: websocket channel refused for now');
          return { refused: true };
        }

        if (res.body.rslt.data[0].noCustomer) {
          logDev('PushProvider: session without customer, no websocket channel');
          return { noChannel: true };
        }

        if ((!jwt) || (!wsUrl)) {
          logError(
            'PushProvider: Missing jwt or wsUrl in response',
            'PushProvider.requestWsCredentials.missingFields',
            {
              hasJwt: !!jwt,
              hasWsUrl: !!wsUrl,
              expires,
            },
          );
          return null;
        }

        storeWsCredentials(jwt, wsUrl, expires, authInfo?.rlid);
        logDev(`PushProvider: Stored credentials, expires=${expires}`);

        return { jwt, wsUrl, expires };
      } catch (err) {
        logError(
          `PushProvider: Failed to get credentials: ${err}`,
          'PushProvider.requestWsCredentials.error',
          {
            err: err?.message,
            ...(err?.status && { status: err.status }),
          },
        );
        return null;
      }
    },
    [cstmrPost, authInfo?.rlid],
  );

  const wsConnect = useCallback(
    async () => {
      // Prevent multiple simultaneous connection attempts
      if (wsBusyRef.current) {
        logDev('PushProvider WebSocket already busy, skipping');
        return;
      }

      if (authStatus !== 'loggedIn') {
        logDev('PushProvider wsConnect: User not authenticated');
        return;
      }

      // The town websocket is a per-customer channel: the Paxity server admin
      // session has no customer and opens none
      if (!authInfo?.cuid) {
        logDev('PushProvider wsConnect: session without customer, no websocket');
        return;
      }

      // back-town also refuses WS to a customer admin
      if (wsNoChannelRlidRef.current && (wsNoChannelRlidRef.current === authInfo?.rlid)) {
        logDev('PushProvider wsConnect: this role has no websocket channel');
        return;
      }

      try {
        wsBusyRef.current = true;
        lastAttemptAtRef.current = Date.now();

        dispatch({ type: 'wsConnecting' });

        // Try to use stored credentials, or request new ones.
        // After 2 consecutive failures, discard stored credentials and request fresh ones
        // — the stored session may have been deleted server-side.
        let credentials = (wsFailCountRef.current >= 2)
          ? null
          : getStoredWsCredentials(authInfo?.rlid);
        if (!credentials) {
          clearWsCredentials();
          credentials = await requestWsCredentials();
        }

        if (credentials?.noChannel) {
          wsNoChannelRlidRef.current = authInfo?.rlid;
          logDev('PushProvider wsConnect: no websocket channel for this session');
          declareWsDisconnectAndHold();
          return;
        }

        if (credentials?.refused) {
          declareWsDisconnectAndHold('Channel refused', wsRetryDelayMs({ refused: true }));
          return;
        }

        if (!credentials) {
          credentialFailCountRef.current += 1;
          logError(
            'PushProvider wsConnect: Could not get credentials',
            'PushProvider.wsConnect.noCredentials',
            {
              wsFailCount: wsFailCountRef.current,
              credentialFailCount: credentialFailCountRef.current,
            },
          );
          declareWsDisconnectAndHold(
            'Could not get credentials',
            wsRetryDelayMs({ failCount: credentialFailCountRef.current }),
          );
          return;
        }
        credentialFailCountRef.current = 0;

        const { jwt, wsUrl } = credentials;

        // Get rlid for sanity check
        const rlid = authInfo?.rlid;
        if (!rlid) {
          logError(
            'PushProvider wsConnect: Missing rlid',
            'PushProvider.wsConnect.missingRlid',
            {
              hasJwt: !!jwt,
              wsUrl,
            },
          );
          declareWsDisconnectAndHold('Missing rlid');
          return;
        }

        logDev(`PushProvider WebSocket connecting to ${wsUrl}`);

        // Pass JWT and trimmed rlid in Sec-WebSocket-Protocol header
        // Format: authorization.bearer.<jwt>.rlid.<encoded_rlid>
        // This keeps auth data out of URLs (which can be logged/snooped)
        const encodedRlid = trimAndEncodeRlidForProtocol(rlid);
        const ws = new WebSocket(wsUrl, [`authorization.bearer.${jwt}.rlid.${encodedRlid}`]);
        let wasOpened = false;

        ws.onopen = () => {
          wasOpened = true;
          wsFailCountRef.current = 0;
          logDev('PushProvider WebSocket connected');
          dispatch({ type: 'wsConnected' });
        };

        ws.onclose = (event) => {
          logDev(`PushProvider WebSocket closed: code=${event.code}, reason=${event.reason}`);
          if (!wasOpened) wsFailCountRef.current += 1;
          // Clear credentials if connection never established (handshake rejected)
          // or on specific auth-related close codes.
          // Not all browsers use the same close code for failed handshakes,
          // so we always clear when the connection was never opened.
          if ((!wasOpened)
              || (event.code === 1006) || (event.code === 4001) || (event.code === 4002)) {
            clearWsCredentials();
          }
          // The session ended on the server: its credentials are dead, and so may be the home JWT
          if (event.code === 4011) {
            clearWsCredentials();
            // Use void to make it clear we do not want to wait this async function:
            void recheckEndedSession();
          }
          declareWsDisconnectAndHold(undefined, wsRetryDelayMs({ failCount: wsFailCountRef.current }));
        };

        ws.onerror = (event) => {
          // A socket that opened and then died is a reload or a blip, and wsConnect self-heals two
          const worthReporting = (!wasOpened) && (wsFailCountRef.current >= 2);
          logError(
            `PushProvider WebSocket error: ${displayJson(event, 2)}`,
            (worthReporting) ? 'PushProvider.wsConnect.wsError' : undefined,
            {
              wsUrl,
              wasOpened,
              wsFailCount: wsFailCountRef.current,
            },
          );
          // Also clear here — on some browsers only onerror fires, not onclose
          if (!wasOpened) clearWsCredentials();
          declareWsDisconnectAndHold('Connection error', wsRetryDelayMs({ failCount: wsFailCountRef.current }));
        };

        ws.onmessage = (event) => {
          try {
            const message = JSON.parse(event.data);
            logDev(`PushProvider WebSocket message: ${displayJson(message)}`);

            // Messages are wrapped: { target, data: { type, ...payload } }
            const { data } = message;
            if (!data?.type) return;

            dispatchEvent(data.type, data, 'ws');
          } catch (err) {
            logError(
              `PushProvider WebSocket message parse error: ${err}`,
              'PushProvider.wsConnect.parseError',
              {
                err: err?.message,
                dataPreview: event?.data?.slice?.(0, 120),
              },
            );
          }
        };

        wsRef.current = ws;
      } catch (err) {
        logError(
          `PushProvider WebSocket connect error: ${err}`,
          'PushProvider.wsConnect.connectError',
          {
            err: err?.message,
            ...(err?.status && { status: err.status }),
          },
        );
        declareWsDisconnectAndHold(err.message);
      }
    },
    [
      authInfo?.rlid,
      authInfo?.cuid,
      authStatus,
      declareWsDisconnectAndHold,
      requestWsCredentials,
      dispatchEvent,
      recheckEndedSession,
    ],
  );

  // A long wait ends as soon as the websocket has a chance again: the network is
  // back, or the user returns to the tab
  useEffect(
    () => {
      const retryNow = () => {
        if ((Date.now() - lastAttemptAtRef.current) >= retryOnReturnMinGapMs) endWsHold();
      };
      const retryWhenVisible = () => {
        if (document.visibilityState === 'visible') retryNow();
      };

      window.addEventListener('online', retryNow);
      document.addEventListener('visibilitychange', retryWhenVisible);

      return () => {
        window.removeEventListener('online', retryNow);
        document.removeEventListener('visibilitychange', retryWhenVisible);
      };
    },
    [endWsHold],
  );

  // Another tab logging in swaps the session under this one: the socket and the
  // stored credentials belong to the role we no longer are.
  useEffect(
    () => {
      const rlid = authInfo?.rlid;

      if (wsRlidRef.current && (wsRlidRef.current !== rlid)) {
        logDev('PushProvider: role changed, dropping the websocket');
        clearWsCredentials();
        wsNoChannelRlidRef.current = undefined;
        if (wsRef.current) {
          wsRef.current.close();
          wsRef.current = null;
        }
      }
      wsRlidRef.current = rlid;
    },
    [authInfo?.rlid],
  );

  // Monitor auth state and connect WebSocket when user is authenticated
  useEffect(
    () => {
      if (authStatus === 'loggedIn') {
        setWasAuthenticated(true);

        if ((!wsConnected)
          && (!wsConnecting)
          && (!wsHold)
          && (!wsRef.current)
          && (!wsBusyRef.current)) {
          logDev('PushProvider: User authenticated, connecting WebSocket...');
          // Use void to make it clear we do not want to wait this async function:
          void wsConnect();
        }
      } else if (wasAuthenticated && (authStatus === 'loggedOut')) {
      // User was authenticated but is no longer -> actual logout during this session
        logDev('PushProvider: User logged out, closing WebSocket and clearing credentials');
        if (wsRef.current) {
          wsRef.current.close();
          wsRef.current = null;
        }
        clearWsCredentials();
        wsNoChannelRlidRef.current = undefined;
        setWasAuthenticated(false);
      }
    },
    [authStatus, wsConnected, wsConnecting, wsConnect, wsHold, wasAuthenticated],
  );

  // Cleanup on logout
  // Ensure WS is disconnected and credentials cleared when we're certain user is logged out
  // This handles cases like: page refresh with stale credentials, or initial load already
  useEffect(
    () => {
      if (authStatus === 'loggedOut') {
        logDev('PushProvider: authStatus is loggedOut, ensuring cleanup');
        if (wsRef.current) {
          wsRef.current.close();
          wsRef.current = null;
        }
        clearWsCredentials();
        wsNoChannelRlidRef.current = undefined;
        setWasAuthenticated(false);
      }
    },
    [authStatus],
  );

  //
  // ServiceWorker (WebPush) message handling
  //
  useEffect(
    () => {
      if ((!navigator) || (!navigator.serviceWorker)) {
        logDev('PushProvider: ServiceWorker not available');
        dispatch({
          type: 'swAvailable',
          available: false,
        });
        return undefined;
      }

      if (!navigator.serviceWorker.addEventListener) {
        logDev('PushProvider: ServiceWorker.addEventListener not available');
        dispatch({
          type: 'swAvailable',
          available: false,
        });
        return undefined;
      }

      dispatch({
        type: 'swAvailable',
        available: true,
      });

      const handleSwMessage = (event) => {
        // Handle push notifications forwarded by service worker
        if (event.data?.type === 'NOTIF') {
          const {
            title,
            body,
            icon,
            data: notifData,
          } = (event.data.notif ?? {});

          if (notifData?.type) {
            // Normalize: flatten server-provided fields into data
            // Map body -> msgTxt and icon -> imgUrl for consistency with WS messages
            const normalized = {
              ...notifData,
              title,
              msgTxt: body || notifData.msgTxt,
              imgUrl: icon || notifData.imgUrl,
            };

            // logDev(`PushProvider ServiceWorker NOTIF: ${displayJson(normalized)}`);
            dispatchEvent(notifData.type, normalized, 'sw');
          }
        }
      };

      navigator.serviceWorker.addEventListener('message', handleSwMessage);

      return () => {
        navigator.serviceWorker.removeEventListener('message', handleSwMessage);
      };
    },
    [dispatchEvent],
  );

  // Disconnect WebSocket on unmount
  useEffect(
    () => () => {
      if (wsRef.current) {
        wsRef.current.close();
      }
    },
    [],
  );

  const contextValue = useMemo(
    () => ({
      push: {
        // Unified subscribe API
        subscribe,
        // Connection status. wsConnected is false while delivery is paused, so
        // consumers fall back to polling and refresh once it resumes
        wsConnected: wsConnected && (!wsPaused),
        wsSocketOpen: wsConnected,
        wsConnecting,
        wsError,
        swAvailable,
        // Manual controls (rarely needed)
        wsConnect,
        wsDisconnect,
      },
    }),
    [subscribe, wsConnected, wsPaused, wsConnecting, wsError, swAvailable, wsConnect, wsDisconnect],
  );

  return (
    <PushContext.Provider value={contextValue}>
      {children}
    </PushContext.Provider>
  );
}

PushProvider.propTypes = {
  children: PropTypes.node.isRequired,
};

export default PushProvider;
